import React, { useState, useCallback, useMemo } from 'react';

import Loadable from 'react-loadable';
import { useDispatch } from 'react-redux';

import { Box } from '@atlaskit/primitives';

import Loader from 'src/components/loading';
import { ExpandablePanel } from 'src/components/sidebar/src/expandable-panel';
import { useIntl } from 'src/hooks/intl';
import { MediaAuth } from 'src/hooks/media';
import authRequest, { jsonHeaders } from 'src/utils/fetch';

import { useAttachments } from '../../hooks/attachments';

import * as flags from './attachments-panel-flags';
import AttachmentsPanelHeader from './attachments-panel-header';
import { messages } from './attachments-panel.i18n';

type BulkAddAttachmentsParams = {
  pullRequestId: number;
  repositoryFullSlug: string;
  mediaIds: string[];
};

const bulkAddAttachments = ({
  repositoryFullSlug,
  pullRequestId,
  mediaIds,
}: BulkAddAttachmentsParams): Promise<any> => {
  if (!mediaIds.length) {
    return Promise.reject();
  }

  return fetch(
    authRequest(
      `/!api/internal/repositories/${repositoryFullSlug}/pullrequests/${pullRequestId}/bulk_attachments/`,
      {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify(mediaIds.map(id => ({ media_id: id }))),
      }
    )
  );
};

type DeleteAttachmentParams = {
  pullRequestId: number;
  repositoryFullSlug: string;
  attachmentId: string;
};
const deletePRAttachment = ({
  repositoryFullSlug,
  pullRequestId,
  attachmentId,
}: DeleteAttachmentParams): Promise<any> => {
  return fetch(
    authRequest(
      `/!api/internal/repositories/${repositoryFullSlug}/pullrequests/${pullRequestId}/attachments/${attachmentId}`,
      {
        method: 'DELETE',
        headers: jsonHeaders,
      }
    )
  );
};

const ATTACHMENTS_KEY = 'pull-request.overview.attachments.expanded';

type Props = {
  repositoryFullSlug: string;
  pullRequestId: number;
  mediaAuth: MediaAuth;
};

function AttachmentPanel(props: Props) {
  const { repositoryFullSlug, pullRequestId, mediaAuth } = props;
  const intl = useIntl();

  const {
    attachments,
    fetchAttachments,
    hasError: hasAttachmentError,
    isLoading: isLoadingAttachments,
  } = useAttachments({
    pullRequestId,
    repositoryFullSlug,
  });

  const Header = (
    <AttachmentsPanelHeader
      count={attachments.length}
      hasError={hasAttachmentError || mediaAuth.hasError}
    />
  );
  const [isUpdatingAttachments, setIsUpdatingAttachments] = useState(false);

  const dispatch = useDispatch();

  const showUpdateError = useCallback(
    () => dispatch(flags.updateErrorAction),
    [dispatch]
  );
  const showRemoveSuccess = useCallback(
    () => dispatch(flags.removeSuccessAction),
    [dispatch]
  );
  const showAddSuccess = useCallback(
    () => dispatch(flags.addSuccessAction),
    [dispatch]
  );

  const showAddError = useCallback(
    () => dispatch(flags.addErrorAction),
    [dispatch]
  );
  const showRemoveError = useCallback(
    () => dispatch(flags.removeErrorAction),
    [dispatch]
  );

  const fetchAttachmentsAfterUpdate = useCallback(() => {
    fetchAttachments()
      .catch(() => showUpdateError())
      .finally(() => setIsUpdatingAttachments(false));
  }, [fetchAttachments, showUpdateError]);

  const addAttachments = (mediaIds: string[]) => {
    setIsUpdatingAttachments(true);
    const params = { repositoryFullSlug, pullRequestId, mediaIds };
    bulkAddAttachments(params).then(
      () => {
        showAddSuccess();
        fetchAttachmentsAfterUpdate();
      },
      () => showAddError()
    );
  };

  const deleteAttachment = useCallback(
    (attachmentId: string) => {
      setIsUpdatingAttachments(true);
      const params = { repositoryFullSlug, pullRequestId, attachmentId };
      deletePRAttachment(params).then(
        () => {
          showRemoveSuccess();
          fetchAttachmentsAfterUpdate();
        },
        () => showRemoveError()
      );
    },
    [
      repositoryFullSlug,
      pullRequestId,
      showRemoveError,
      showRemoveSuccess,
      fetchAttachmentsAfterUpdate,
    ]
  );

  // The panel should default to expanded if there are any attachments,
  // otherwise return undefined to default to whatever value is in local storage
  const defaultExpanded = useMemo(
    () => !!attachments.length || undefined,
    [attachments.length]
  );

  const AttachmentsPanelContent = Loadable({
    loader: () =>
      import(
        /* webpackChunkName: "pull-request-attachments" */ 'src/sections/repository/sections/pull-request/components/attachments/attachments-panel-content'
      ),
    loading: () => <Loader size="small" />,
  });

  return (
    <Box
      as="section"
      aria-label={intl.formatMessage(messages.attachmentListLabel)}
      role="region"
    >
      <ExpandablePanel
        header={Header}
        defaultExpanded={defaultExpanded}
        storageKey={ATTACHMENTS_KEY}
      >
        <AttachmentsPanelContent
          addAttachments={addAttachments}
          deleteAttachment={deleteAttachment}
          attachments={attachments}
          collectionName={mediaAuth.collectionName || ''}
          hasError={hasAttachmentError || mediaAuth.hasError}
          fetchAttachments={fetchAttachments}
          isLoading={
            isLoadingAttachments || mediaAuth.isLoading || isUpdatingAttachments
          }
          mediaClientConfig={mediaAuth.mediaClientConfig}
        />
      </ExpandablePanel>
    </Box>
  );
}

export default React.memo(AttachmentPanel);
